import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  LinearProgress,
  Skeleton,
  Chip,
} from '@mui/material';
import { motion } from 'framer-motion';
import COLORS from '../../../../../../constants/colors';
import { Link } from 'react-router-dom';
import baseURL from '../../../../user/config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

import { PrimaryButton, OutlinedButton } from '../../../../../Reusable/Button'; 


// --- Caching Constants ---
const CAMPAIGNS_STORAGE_KEY = 'cached_home_campaigns_top6';
// Set cache duration to 5 minutes (5 * 60 * 1000 milliseconds)
const CACHE_DURATION = 5 * 60 * 1000; 


// --- Helper Functions ---
const fmt = (amt) => 'UGX ' + Math.floor(amt).toLocaleString();

const sentenceCase = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';

const truncateText = (text, wordLimit = 20) => {
  if (!text) return "";
  const words = text.split(/\s+/);
  if (words.length <= wordLimit) return words.join(" ");
  return words.slice(0, wordLimit).join(" ") + "...";
};

// Helper function to sort by amount raised (descending)
const sortByRaisedAmount = (data) => data.sort((a, b) => b.raised - a.raised); 


const CampaignGrid = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Data Fetching and Caching Logic ---
  useEffect(() => {
    const fetchAndCacheCampaigns = async () => {
      let isCacheStale = true;

      // Processing function: Filters closed campaigns, then sorts by raised amount.
      const processCampaigns = (data) => {
          // 1. Filter: Keep only open campaigns
          const openCampaigns = data.filter(c => c.status !== 'closed');
          // 2. Sort: Rank by raised amount
          return sortByRaisedAmount(openCampaigns);
      };

      // 1. Try to load from cache
      try {
        const cachedData = localStorage.getItem(CAMPAIGNS_STORAGE_KEY);
        if (cachedData) {
          const { data, timestamp } = JSON.parse(cachedData);
          const now = new Date().getTime();

          if (now - timestamp < CACHE_DURATION) {
            // Cache is fresh, process and use it!
            const sortedAndFiltered = processCampaigns(data);
            setCampaigns(sortedAndFiltered);
            setLoading(false);
            isCacheStale = false;
          }
        }
      } catch (e) {
        console.error('Error reading/parsing campaign cache:', e);
      }

      // 2. Fetch data if cache is stale or non-existent
      if (isCacheStale) {
        setLoading(true);
        try {
          // Fetch limit=6 campaigns
          const res = await fetch(`${baseURL}/data/backend/campaigns?limit=6`);
          if (!res.ok) throw new Error('Failed to fetch campaigns');
          const data = await res.json();

          // Apply processing logic to the fetched data
          const sortedAndFiltered = processCampaigns(data); 

          setCampaigns(sortedAndFiltered);
          
          // 3. Store fresh data in cache
          const cacheItem = {
            data: data, // Store the raw data
            timestamp: new Date().getTime(),
          };
          localStorage.setItem(CAMPAIGNS_STORAGE_KEY, JSON.stringify(cacheItem));

        } catch (e) {
          setError(e.message);
          console.error('Error fetching campaigns:', e);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchAndCacheCampaigns();
  }, []); 

  // --- Skeleton Loader Component ---
  const SkeletonCard = () => (
    <Card
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        width: '100%',
        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
        borderRadius: 2,
        p: 0,
        overflow: 'hidden',
        backgroundColor: '#fff',
        position: 'relative',
      }}
    >
      <Skeleton variant="rectangular" width="100%" height={220} />
      <CardContent
        sx={{
          p: 2.5,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          flexGrow: 1,
        }}
      >
        <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1, textAlign: 'left' }} />
        <Skeleton variant="text" width="100%" height={20} />
        <Skeleton variant="text" width="90%" height={20} />
        <Skeleton variant="text" width="70%" height={20} sx={{ mb: 2 }} />

        <Skeleton variant="text" width="60%" height={20} sx={{ mt: 2, textAlign: 'left' }} />
        <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: 10, mb: 1 }} />
        <Skeleton variant="text" width="30%" height={16} sx={{ textAlign: 'right', alignSelf: 'flex-end' }} />

        <Box mt={3} display="flex" justifyContent="space-between" alignItems="center">
          <Skeleton variant="rectangular" width="45%" height={40} sx={{ borderRadius: 20 }} />
          <Skeleton variant="text" width="30%" height={20} />
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
      <Typography variant="h4" fontWeight={800} color={COLORS.primary} mb={8}>
        Our Top Active Fundraising Campaigns
      </Typography>

      {loading ? (
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Grid container spacing={3} justifyContent="center">
            {/* Show 6 skeleton cards while loading */}
            {[...Array(6)].map((_, index) => ( 
              <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: 'flex' }}>
                <SkeletonCard />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : error ? (
        <Typography color="error">{error}</Typography>
      ) : campaigns.length === 0 ? (
        <Box mt={4}>
          <Typography variant="h6" color={COLORS.text}>
            No active fundraising campaigns found.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Stay tuned! Fundraising campaigns will appear here soon.
          </Typography>
        </Box>
      ) : (
        <>
          <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
            <Grid container spacing={3} justifyContent="center">
              {/* Note: campaign.status will NEVER be 'closed' here due to the filter */}
              {campaigns.slice(0, 6).map((c) => { 
                const closed = c.status === 'closed'; // This will always be false now
                const pct = (c.raised / c.goal) * 100;

                return (
                  <Grid item xs={12} sm={6} md={4} key={c.id} sx={{ display: 'flex' }}>
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.6 }}
                      style={{ flexGrow: 1, display: 'flex' }}
                    >
                      <Card
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between',
                          height: '100%',
                          width: '100%',
                          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                          borderRadius: 2,
                          p: 0,
                          overflow: 'hidden',
                          backgroundColor: '#fff',
                          position: 'relative',
                          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-6px)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                          },
                        }}
                      >
                        <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                          <Link
                            to={`/campaigns/${c.name}?id=${c.id}`}
                            state={{ campaign: c }}
                            style={{ textDecoration: 'none' }}
                          >
                            <Box
                              component="img"
                              src={c.image_url}
                              alt={c.name}
                              sx={{
                                width: '100%',
                                height: 220,
                                objectFit: 'cover',
                                cursor: 'pointer',
                                transition: 'transform 0.3s ease',
                                '&:hover': { transform: 'scale(1.05)' },
                              }}
                            />
                          </Link>

                          {/* Category badge — top left */}
                          <Box sx={{ position: 'absolute', top: 10, left: 10 }}>
                            {c.owner_type === 'Organization' ? (
                              !!c.accepts_recurring ? (
                                <Chip label="Org · Recurring" size="small"
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(45,74,61,0.88)', color: '#F8B400', border: 'none', backdropFilter: 'blur(4px)' }} />
                              ) : (
                                <Chip label="Organization" size="small"
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(45,74,61,0.88)', color: '#fff', border: 'none', backdropFilter: 'blur(4px)' }} />
                              )
                            ) : (
                              !!c.accepts_recurring ? (
                                <Chip label="Individual · Recurring" size="small"
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(248,180,0,0.92)', color: '#2D4A3D', border: 'none', backdropFilter: 'blur(4px)' }} />
                              ) : (
                                <Chip label="Individual" size="small"
                                  sx={{ fontSize: '0.68rem', fontWeight: 700, bgcolor: 'rgba(248,180,0,0.92)', color: '#2D4A3D', border: 'none', backdropFilter: 'blur(4px)' }} />
                              )
                            )}
                          </Box>
                        </Box>

                        <CardContent
                          sx={{
                            p: 2.5,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            flexGrow: 1,
                          }}
                        >
                          <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={COLORS.primary}
                            gutterBottom
                            sx={{
                              textTransform: 'none',
                              fontSize: '1.1rem',
                              textAlign: 'left'
                            }}
                          >
                            {sentenceCase(c.name)}
                          </Typography>
                          
                          <Box
                            sx={{
                              color: COLORS.textSecondary,
                              "& p:last-child": { mb: 0 },
                              "& ul, & ol": { pl: 2 },
                              textAlign: "left",
                              lineHeight: 1.5,
                            }}
                          >
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeRaw]}
                            >
                              {truncateText(c.description, 20)}
                            </ReactMarkdown>
                          </Box>


                          <Box mt={2}>
                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              sx={{ color: COLORS.primary, mb: 0.5, textAlign: 'left' }}
                            >
                              {fmt(c.raised)} raised of {fmt(c.goal)} goal
                            </Typography>

                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                height: 8,
                                borderRadius: 10,
                                backgroundColor: COLORS.border,
                                '& .MuiLinearProgress-bar': {
                                  // Color is primary since only open campaigns are shown
                                  backgroundColor: COLORS.primary,
                                },
                              }}
                            />

                            <Box sx={{ textAlign: 'right', mt: 0.5 }}>
                              <Typography
                                variant="caption"
                                sx={{ color: COLORS.text }}
                              >
                                {Math.min(Math.floor(pct), 100)}% funded
                              </Typography>
                            </Box>

                          </Box>

                          <Box
                            mt={3}
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <PrimaryButton
                              component={Link}
                              to={`/campaigns/${c.name}?id=${c.id}`}
                              state={{ campaign: c }}
                            >
                              Donate Now
                            </PrimaryButton>

                            <Typography
                              variant="body2"
                              fontWeight="bold"
                              color={COLORS.text}
                              sx={{ fontSize: '0.75rem' }}
                            >
                              {c.donations} Donations
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>

            <Box textAlign="right" mt={4}>
              <OutlinedButton
                component={Link}
                to="/campaigns"
                
              >
                View All Campaigns
              </OutlinedButton>
            </Box>
          </Box>


        </>
      )}
    </Box>
  );
};

export default CampaignGrid;